import { orderApi } from "src/services/api";
import {
  GetOrderDto,
  CreateUserOrderDTO,
  UpdateDeliveryTypeDto,
  OrderControllerAddOrderPhotosRequest,
} from "src/services/generated-api";

// BE Issue #1: CreateOrder returns GetOrderDto but is typed as AxiosResponse<void>
type OrderApiResponse = { data: GetOrderDto };

// BE Issue #8: photoSizeId should be optional in CreateUserOrderDTO
type CreateOrderPayload = Omit<CreateUserOrderDTO, "photoSizeId"> & {
  photoSizeId?: string | null;
};

export async function createOrder(
  payload: CreateOrderPayload
): Promise<GetOrderDto> {
  const response = (await orderApi.orderControllerCreateOrder(
    payload as CreateUserOrderDTO,
    "",
    // x-client-channel: the web app leaves this unset; the server derives the
    // channel from the domain. Mobile is the caller that sends it explicitly.
    undefined,
    { withCredentials: true }
  )) as unknown as OrderApiResponse;
  return response.data;
}

export async function fetchOrder(
  orderId: string,
  guestToken: string
): Promise<GetOrderDto> {
  const response = await orderApi.getOneBaseOrderControllerOrder(
    orderId,
    guestToken,
    undefined,
    // Explicitly join product-with-media (greeting-card) lines so the cart drawer
    // can render them. The other relations are included to preserve the previous
    // default-eager behavior.
    [
      "orderPhotos",
      "orderProductDetails",
      "orderCalendars",
      "productWithMediaOrders",
      "productWithMediaOrders.definition",
    ],
    undefined
  );
  return response.data as GetOrderDto;
}

/** The few fields a page needs to decide what an order IS, not show it. */
export type OrderHead = Pick<GetOrderDto, "id" | "kind" | "status" | "amount" | "domain">;

/**
 * The order's kind, status, amount and domain only — for deciding which page
 * a Stripe return lands on (gift card or goods) before the goods checkout
 * fetches the full order itself.
 *
 * `fields` narrows the order's own columns; the backend's getOne override
 * still joins the detail relations (it adds them per request), so this is
 * lighter, not free. The crud parser reads only the first `fields` value,
 * hence one comma-joined entry rather than an array of names.
 */
export async function fetchOrderHead(orderId: string, guestToken: string): Promise<OrderHead> {
  const response = await orderApi.getOneBaseOrderControllerOrder(
    orderId,
    guestToken,
    ["kind,status,amount,domain"],
    undefined,
    undefined,
  );
  const { id, kind, status, amount, domain } = response.data as GetOrderDto;
  return { id, kind, status, amount, domain };
}

// Upsert — if the productId is already in the order, the backend updates
// the existing line's quantity instead of inserting a duplicate row.
export async function addOrderProduct(
  orderId: string,
  payload: { productId: string; quantity: number },
  guestToken: string
): Promise<GetOrderDto> {
  const response = (await orderApi.orderControllerUpsertOrderProduct(
    orderId,
    payload,
    guestToken
  )) as unknown as OrderApiResponse;
  return response.data;
}

export async function updateOrderProductQuantity(
  orderId: string,
  payload: { orderProductDetailId: string; quantity: number },
  guestToken: string
): Promise<GetOrderDto> {
  const response = (await orderApi.orderControllerUpdateOrderProductQuantity(
    orderId,
    payload.orderProductDetailId,
    { quantity: payload.quantity },
    guestToken
  )) as unknown as OrderApiResponse;
  return response.data;
}

export async function removeOrderProductDetail(
  orderId: string,
  payload: { orderProductDetailId: string },
  guestToken: string
): Promise<GetOrderDto> {
  const response = (await orderApi.orderControllerRemoveOrderProduct(
    orderId,
    payload.orderProductDetailId,
    guestToken
  )) as unknown as OrderApiResponse;
  return response.data;
}

export async function removeOrderPhoto(
  orderId: string,
  orderPhotoId: string,
  guestToken: string
): Promise<GetOrderDto> {
  const response = (await orderApi.orderControllerRemoveOrderPhotoUnified(
    orderId,
    orderPhotoId,
    guestToken
  )) as unknown as OrderApiResponse;
  return response.data;
}

export async function setDeliveryType(
  orderId: string,
  payload: UpdateDeliveryTypeDto,
  guestToken: string
): Promise<GetOrderDto> {
  const response = await orderApi.orderControllerSetDeliveryType(
    orderId,
    payload,
    guestToken
  );
  return response.data as GetOrderDto;
}

export async function addOrderPhotoFromExisting(
  orderId: string,
  payload: OrderControllerAddOrderPhotosRequest,
  guestToken: string
): Promise<GetOrderDto> {
  const response = (await orderApi.orderControllerAddOrderPhotosUnified(
    orderId,
    payload,
    guestToken
  )) as unknown as OrderApiResponse;
  return response.data;
}

export async function createOrderReceipt(
  orderId: string,
  guestToken: string
): Promise<GetOrderDto> {
  const response = (await orderApi.orderControllerCreateReceipt(
    orderId,
    guestToken
  )) as unknown as OrderApiResponse;
  return response.data;
}

/**
 * Change how many copies of a greeting-card / invitation line the order wants.
 *
 * The backend rejects anything below the definition's minQuantity, so callers
 * must offer "remove" rather than a decrement once the line sits at the floor.
 *
 * Typed `void` by the generator (the controller has no @ApiResponse type) but
 * returns the recalculated order — same mismatch as BE Issue #1 above.
 */
export async function updateProductWithMediaQuantity(
  orderId: string,
  lineId: string,
  quantity: number,
  guestToken: string
): Promise<GetOrderDto> {
  const response = (await orderApi.orderControllerUpdateProductWithMediaQuantity(
    orderId,
    lineId,
    { quantity },
    guestToken
  )) as unknown as OrderApiResponse;
  return response.data;
}
